/**
 * Announcement bar enhancer for sections/announcement-bar.liquid (Phase 1).
 *
 * DOM-only and OUTSIDE CCP: no event bus, no PPD, no md-config-* imports.
 * Liquid is the source of truth for what renders; this file only:
 *
 * 1. EXPIRY GUARD. Every scheduled offer carries `data-ends-at` (Unix seconds,
 *    the same end epoch the Liquid date filter uses). A CDN-cached page can
 *    still contain an offer that has ended, so on load, when a countdown fires
 *    `countdown:ended`, at the next known end time, and on a bfcache restore,
 *    every announcement with `now >= endsAt` is removed (same boundary as
 *    assets/countdown-timer.js). Then:
 *      - 2+ slides left: renumber the "N of M" labels and tracking positions,
 *        and ask SlideshowComponent.refreshSlides() to re-read its cache.
 *      - 1 left: replace the carousel with the plain single-announcement
 *        markup (no carousel roles, no controls, no autoplay).
 *      - 0 left: remove the announcement; if the bar then holds nothing else
 *        (no social icons, no selectors), hide the whole section and fire
 *        `header:update` so assets/sticky-scroll-activation.js re-measures the
 *        header with getBoundingClientRect().bottom.
 *    The slide that was showing stays showing when it survives (else the next
 *    survivor). Focus inside the bar is never dropped to <body>: it returns to
 *    the same element if it survived, else to the replacement bar (as a
 *    tabindex=-1 region), else to the first focusable element in the header
 *    after the bar (or the main landmark).
 *    Skipped in the Theme Editor, which always renders fresh Liquid.
 *    Expiry is NOT a tracked event (no customer action).
 *
 * 2. COPY CODE. The copy <button> is server-rendered `hidden` and revealed only
 *    when navigator.clipboard.writeText is usable. Results are announced in
 *    the persistent #md-announcement-status region (layout/theme.liquid,
 *    outside the section re-render boundary): clear, then write after a
 *    cancellable 100ms timer, so a repeat copy cancels the pending write and
 *    only the latest result is spoken. Failure is announced too, and shown
 *    visibly next to the button with the code itself.
 *
 * 5. DISMISS (Phase 2, opt-in per block). A dismissible announcement carries
 *    `data-dismiss-key` = `md-ab-dismissed:<block id>:<end epoch | none>` and a
 *    native close <button data-announcement-dismiss>. A click stores the key
 *    in sessionStorage (try/catch: private mode or a blocked store still hides
 *    it for this page view via an in-memory set) and removes the announcement
 *    through the SAME path as the expiry guard: renumber, collapse at 1, hide
 *    the section + header:update at 0, focus per the contract above (never
 *    <body>). On load, dismissed keys are removed by the same prune() run; the
 *    section's inline pre-paint check has already marked them
 *    `data-ab-dismissed` (CSS-hidden) so nothing paints and nothing shifts.
 *    Keying on the end epoch means a merchant who extends a sale shows it
 *    again. Tracked declaratively (dismiss_announcement). Disabled in the
 *    Theme Editor, like the expiry guard.
 *
 * 4. APPLY REDIRECT. When the auto-apply link falls back to the current page,
 *    its redirect gains the current query string (same-origin relative only).
 *
 * 3. CONTROL TRACKING LABELS. Prev/next/pause carry the CURRENT slide's block
 *    id and position (`slideChanged` from SliderComponent), so a click reports
 *    which announcement the shopper was on. Clicks themselves are tracked by
 *    the declarative bridge in assets/analytics-tracking.js.
 *
 * Section reloads in the Theme Editor re-run init for the new section, and
 * this file may be evaluated more than once, so it is guarded.
 */
(function () {
  'use strict';

  if (window.MdAnnouncementBar) {
    window.MdAnnouncementBar.init(document);
    return;
  }

  var STATUS_ID = 'md-announcement-status';
  var ANNOUNCE_DELAY_MS = 100;
  var SUCCESS_VISIBLE_MS = 4000;
  var MAX_TIMEOUT_MS = 2147483647;

  var announceTimer = null;
  var copySeq = 0;
  var expiryTimer = null;
  // Keys dismissed during this page view, so a dismiss still works when
  // sessionStorage throws (Safari private mode, blocked storage).
  var dismissedKeys = Object.create(null);

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function endsAtMs(el) {
    var seconds = Number(el.getAttribute('data-ends-at'));
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
  }

  function isExpired(el, now) {
    var end = endsAtMs(el);
    return end !== null && now >= end;
  }

  /* --------------------------------------------------------------- dismiss */

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key) {
    try {
      window.sessionStorage.setItem(key, '1');
      return true;
    } catch (e) {
      return false;
    }
  }

  function isDismissed(el) {
    var key = el.getAttribute('data-dismiss-key');
    if (!key) return false;
    return (
      el.hasAttribute('data-ab-dismissed') ||
      dismissedKeys[key] === true ||
      storageGet(key) !== null
    );
  }

  /** The single removal predicate shared by expiry and dismiss. */
  function shouldRemove(el, now) {
    return isExpired(el, now) || isDismissed(el);
  }

  /* ---------------------------------------------------------------- expiry */

  function notifyHeaderChanged() {
    if (!document.body) return;
    document.body.dispatchEvent(
      new CustomEvent('header:update', { bubbles: true })
    );
  }

  /** Does the utility bar still show anything once `removed` is gone? */
  function barHasOtherContent(utilityBar) {
    return !!utilityBar.querySelector(
      '[data-announcement-single], [data-announcement-carousel], .list-social, localization-form, .localization-form'
    );
  }

  function removeAnnouncement(el) {
    var utilityBar = el.closest('.utility-bar');
    var section = el.closest('.shopify-section');
    el.remove();
    if (utilityBar && !barHasOtherContent(utilityBar) && section) {
      section.hidden = true;
      // .shopify-section may carry a display rule that beats the UA [hidden].
      section.style.display = 'none';
    }
    notifyHeaderChanged();
  }

  function setTrackPosition(scope, position) {
    scope.querySelectorAll('[data-track-position]').forEach(function (node) {
      if (node.hasAttribute('data-announcement-control')) return;
      node.setAttribute('data-track-position', String(position));
    });
  }

  function syncControls(carousel, slide, position) {
    if (!slide) return;
    var id = slide.getAttribute('data-announcement-id') || '';
    carousel
      .querySelectorAll('[data-announcement-control]')
      .forEach(function (control) {
        control.setAttribute('data-track-label', id);
        control.setAttribute('data-track-position', String(position));
      });
  }

  function getSlides(carousel) {
    return Array.prototype.slice.call(
      carousel.querySelectorAll('.slideshow__slide[id^="Slide-"]')
    );
  }

  function renumber(carousel, slides) {
    var of = carousel.getAttribute('data-of-label') || 'of';
    slides.forEach(function (slide, index) {
      slide.setAttribute(
        'aria-label',
        index + 1 + ' ' + of + ' ' + slides.length
      );
      setTrackPosition(slide, index + 1);
    });
  }

  /** Replace a carousel left with one slide by the single-announcement markup. */
  function collapseToSingle(carousel, slide) {
    var single = document.createElement('div');
    var classes = ['announcement-bar'];
    var extra = carousel.getAttribute('data-one-announcement-class');
    if (extra) classes.push(extra);
    Array.prototype.forEach.call(slide.classList, function (cls) {
      if (/^announcement-bar--.+-animation$/.test(cls)) classes.push(cls);
    });
    var content =
      slide.querySelector('.announcement-bar__announcement') || slide;
    if (content.classList.contains('announcement-bar__announcement--dismissible')) {
      classes.push('announcement-bar--dismissible');
    }
    single.className = classes.join(' ');
    single.setAttribute('role', 'region');
    single.setAttribute(
      'aria-label',
      carousel.getAttribute('data-single-label') || ''
    );
    single.setAttribute('data-announcement-single', '');
    ['data-ends-at', 'data-dismiss-key', 'data-announcement-id'].forEach(
      function (attr) {
        if (slide.hasAttribute(attr)) {
          single.setAttribute(attr, slide.getAttribute(attr));
        }
      }
    );
    while (content.firstChild) single.appendChild(content.firstChild);
    setTrackPosition(single, 1);
    // Disconnecting the component runs its disconnectedCallback (autoplay off).
    carousel.replaceWith(single);
    notifyHeaderChanged();
    return single;
  }

  function pruneCarousel(carousel, now) {
    var slides = getSlides(carousel);
    // Which slide is showing BEFORE anything is removed: keep showing it if it
    // survives, else the next survivor after it (wrapping to the first).
    var currentIndex = Math.min(
      Math.max((Number(carousel.currentPage) || 1) - 1, 0),
      slides.length - 1
    );
    var expired = slides.map(function (slide) {
      return shouldRemove(slide, now);
    });
    if (expired.indexOf(true) === -1) return;
    var keepSlide = null;
    for (var step = 0; step < slides.length; step++) {
      var candidate = (currentIndex + step) % slides.length;
      if (!expired[candidate]) {
        keepSlide = slides[candidate];
        break;
      }
    }
    var remaining = slides.filter(function (slide, index) {
      if (!expired[index]) return true;
      slide.remove();
      return false;
    });

    if (remaining.length === 0) {
      removeAnnouncement(carousel);
      return;
    }
    if (remaining.length === 1) {
      collapseToSingle(carousel, remaining[0]);
      return;
    }
    renumber(carousel, remaining);
    var page = remaining.indexOf(keepSlide) + 1 || 1;
    if (typeof carousel.refreshSlides === 'function') {
      carousel.refreshSlides(page);
    }
    syncControls(carousel, remaining[page - 1], page);
  }

  /* ------------------------------------------------------- focus recovery */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function isShown(el) {
    for (var node = el; node && node !== document; node = node.parentNode) {
      if (node.nodeType !== 1) continue;
      if (node.hidden || node.inert) return false;
      if (node.style && node.style.display === 'none') return false;
    }
    return true;
  }

  /**
   * Focus `el` and report whether it actually took focus. A link hidden by a
   * stylesheet (desktop-only nav at 375px) passes isShown() but silently
   * refuses focus, which would strand focus on <body>.
   */
  function tryFocus(el) {
    focusWithoutScroll(el);
    return document.activeElement === el;
  }

  function focusWithoutScroll(el) {
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function focusAsRegion(el) {
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    focusWithoutScroll(el);
  }

  /**
   * Focus target once the whole bar is gone: the first focusable element in
   * the header group AFTER the bar's section (the header logo), else the main
   * content landmark. Never <body>.
   */
  function focusAfterHeaderBar(section) {
    var groups = document.querySelectorAll(
      '.shopify-section-group-header-group'
    );
    var past = !section;
    for (var i = 0; i < groups.length; i++) {
      if (groups[i] === section) {
        past = true;
        continue;
      }
      if (!past || !isShown(groups[i])) continue;
      var candidates = groups[i].querySelectorAll(FOCUSABLE);
      for (var j = 0; j < candidates.length; j++) {
        if (isShown(candidates[j]) && tryFocus(candidates[j])) return;
      }
    }
    var main =
      document.getElementById('MainContent') || document.querySelector('main');
    if (main) focusAsRegion(main);
  }

  /**
   * Snapshot focus if it is inside an announcement bar. `from` overrides the
   * active element: a dismiss click passes its button, because Safari does not
   * focus a button on a mouse click and the button is about to be removed.
   */
  function captureFocus(host, from) {
    var active = from || document.activeElement;
    if (!active || active === document.body) return null;
    var bar = active.closest(
      '[data-announcement-single], [data-announcement-carousel]'
    );
    if (!bar || !host.contains(bar)) return null;
    return {
      active: active,
      section: bar.closest('.shopify-section'),
      utilityBar: bar.closest('.utility-bar'),
    };
  }

  /**
   * Put focus back after prune() changed the DOM, so a keyboard or screen
   * reader user is never dropped to <body> (WCAG 2.4.3):
   *  1. the element itself, if it survived (it may have been moved);
   *  2. else the announcement bar that replaced it (focusable region);
   *  3. else, the bar being gone, the next focusable thing in the header.
   */
  function restoreFocus(snapshot) {
    if (!snapshot) return;
    var active = snapshot.active;
    if (active.isConnected && isShown(active)) {
      if (document.activeElement !== active) focusWithoutScroll(active);
      return;
    }
    var sectionShown = snapshot.section && isShown(snapshot.section);
    var replacement =
      sectionShown &&
      snapshot.utilityBar &&
      snapshot.utilityBar.querySelector(
        '[data-announcement-single], [data-announcement-carousel]'
      );
    if (replacement) {
      focusAsRegion(replacement);
      return;
    }
    if (sectionShown && snapshot.utilityBar) {
      var others = snapshot.utilityBar.querySelectorAll(FOCUSABLE);
      for (var k = 0; k < others.length; k++) {
        if (isShown(others[k]) && tryFocus(others[k])) return;
      }
    }
    focusAfterHeaderBar(snapshot.section);
  }

  /**
   * Remove every expired or dismissed announcement under `scope`. Returns
   * nothing; safe to call repeatedly. `now` is injectable for tests;
   * `focusFrom` is the element focus is restored from (dismiss button).
   */
  function prune(scope, now, focusFrom) {
    if (isDesignMode()) return;
    var host = scope && scope.querySelectorAll ? scope : document;
    var at = typeof now === 'number' ? now : Date.now();
    var focusSnapshot = captureFocus(host, focusFrom);
    host
      .querySelectorAll('[data-announcement-carousel]')
      .forEach(function (carousel) {
        pruneCarousel(carousel, at);
      });
    host
      .querySelectorAll(
        '[data-announcement-single][data-ends-at], [data-announcement-single][data-dismiss-key]'
      )
      .forEach(function (single) {
        if (shouldRemove(single, at)) removeAnnouncement(single);
      });
    restoreFocus(focusSnapshot);
  }

  /** Arm one timer for the next end time still on the page. */
  function scheduleNextExpiry() {
    if (expiryTimer !== null) clearTimeout(expiryTimer);
    expiryTimer = null;
    if (isDesignMode()) return;
    var now = Date.now();
    var next = null;
    document
      .querySelectorAll(
        '[data-announcement-carousel] .slideshow__slide[data-ends-at], [data-announcement-single][data-ends-at]'
      )
      .forEach(function (el) {
        var end = endsAtMs(el);
        if (end !== null && end > now && (next === null || end < next)) {
          next = end;
        }
      });
    if (next === null) return;
    expiryTimer = setTimeout(
      run,
      Math.min(Math.max(next - now, 0), MAX_TIMEOUT_MS)
    );
  }

  function run() {
    prune(document);
    scheduleNextExpiry();
  }

  /**
   * Dismiss the announcement that owns `button` for this session, then reuse
   * the expiry removal path. No-op in the Theme Editor.
   */
  function dismiss(button) {
    if (isDesignMode()) return;
    var owner = button.closest('[data-dismiss-key]');
    if (!owner) return;
    var key = owner.getAttribute('data-dismiss-key');
    dismissedKeys[key] = true;
    storageSet(key);
    prune(document, undefined, button);
    scheduleNextExpiry();
  }

  /* ------------------------------------------------------------- copy code */

  function clipboardSupported() {
    return !!(
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      window.isSecureContext !== false
    );
  }

  function getStatusRegion() {
    var region = document.getElementById(STATUS_ID);
    if (region) return region;
    // Fallback only: the layout normally renders it. Created empty, so the
    // clear-then-write delay still gives assistive tech a chance to announce.
    region = document.createElement('div');
    region.id = STATUS_ID;
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  }

  /** Clear, then write after a cancellable delay. A newer call cancels an older one. */
  function announce(text) {
    var region = getStatusRegion();
    if (announceTimer !== null) clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () {
      announceTimer = null;
      region.textContent = text;
    }, ANNOUNCE_DELAY_MS);
  }

  function showFeedback(button, text, isError) {
    var row = button.closest('.announcement-bar__code') || button.parentNode;
    var feedback =
      row && row.querySelector('[data-announcement-copy-feedback]');
    if (!feedback) return;
    if (feedback._mdClearTimer) clearTimeout(feedback._mdClearTimer);
    feedback.textContent = text;
    feedback.classList.toggle(
      'announcement-bar__copy-feedback--error',
      isError
    );
    if (!isError) {
      feedback._mdClearTimer = setTimeout(function () {
        feedback.textContent = '';
      }, SUCCESS_VISIBLE_MS);
    }
  }

  function onCopyResult(button, seq, ok) {
    if (seq !== copySeq) return; // a newer copy superseded this one
    var code = button.getAttribute('data-code') || '';
    var text = ok
      ? button.getAttribute('data-label-copied') || 'Code copied'
      : (button.getAttribute('data-label-failed') || 'Copy failed.') +
        ' ' +
        code;
    showFeedback(button, text, !ok);
    announce(text);
  }

  function copyCode(button) {
    var seq = ++copySeq;
    var code = button.getAttribute('data-code') || '';
    var result;
    try {
      result = navigator.clipboard.writeText(code);
    } catch (e) {
      result = Promise.reject(e);
    }
    return Promise.resolve(result).then(
      function () {
        onCopyResult(button, seq, true);
      },
      function () {
        onCopyResult(button, seq, false);
      }
    );
  }

  function onDocumentClick(event) {
    var target =
      event.target && event.target.nodeType === 1
        ? event.target
        : event.target && event.target.parentElement;
    if (!target) return;
    var dismissButton = target.closest('[data-announcement-dismiss]');
    if (dismissButton) {
      dismiss(dismissButton);
      return;
    }
    var button = target.closest('[data-announcement-copy]');
    if (!button) return;
    copyCode(button);
  }

  /* ------------------------------------------------------ apply redirect */

  /**
   * The server-rendered auto-apply link redirects to request.path, which has
   * no query string (Liquid cannot read it). Re-point it at the full current
   * URL. Always a same-origin relative path: pathname must start with a
   * single '/', never '//' (which a browser would read as another host).
   */
  function currentRelativeUrl() {
    var path = window.location.pathname || '/';
    if (path.charAt(0) !== '/' || path.charAt(1) === '/') return null;
    return path + (window.location.search || '');
  }

  function updateApplyRedirects(host) {
    var target = currentRelativeUrl();
    if (!target || !window.location.search) return;
    host.querySelectorAll('a[data-apply-base]').forEach(function (link) {
      var base = link.getAttribute('data-apply-base') || '';
      if (base.charAt(0) !== '/' || base.charAt(1) === '/') return;
      link.setAttribute(
        'href',
        base + '?redirect=' + encodeURIComponent(target)
      );
    });
  }

  /* ------------------------------------------------------------------ init */

  function init(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    if (clipboardSupported()) {
      host
        .querySelectorAll('[data-announcement-copy][hidden]')
        .forEach(function (button) {
          button.hidden = false;
        });
    }
    host
      .querySelectorAll('[data-announcement-carousel]')
      .forEach(function (carousel) {
        if (carousel._mdAnnouncementBound) return;
        carousel._mdAnnouncementBound = true;
        carousel.addEventListener('slideChanged', function (event) {
          var detail = event.detail || {};
          syncControls(carousel, detail.currentElement, detail.currentPage);
        });
      });
    updateApplyRedirects(host);
    run();
  }

  window.MdAnnouncementBar = {
    init: init,
    prune: prune,
    announce: announce,
    copyCode: copyCode,
    dismiss: dismiss,
    updateApplyRedirects: updateApplyRedirects,
    scheduleNextExpiry: scheduleNextExpiry,
    ANNOUNCE_DELAY_MS: ANNOUNCE_DELAY_MS,
  };

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('countdown:ended', run);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) run();
  });
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
